import { useMutation, useQueryClient } from '@tanstack/react-query';
import { expertTipsApi } from '../services/expertTipsApi';
import { expertTipKeys } from './useExpertTips';
import type { FeedbackType, SubmitExpertTipFeedbackResponse } from '../types/expert-tip.types';

interface SubmitFeedbackParams {
  expertTipId: number;
  feedbackType: FeedbackType;
}

/**
 * Hook to submit feedback for an expert tip.
 * Invalidates the tips list on success so statistics update.
 *
 * Usage:
 *   const { mutateAsync: submitFeedback, isPending } = useSubmitExpertTipFeedback();
 *   const result = await submitFeedback({ expertTipId: 1, feedbackType: 'LIKE' });
 */
export function useSubmitExpertTipFeedback() {
  const queryClient = useQueryClient();

  return useMutation<SubmitExpertTipFeedbackResponse, Error, SubmitFeedbackParams>({
    mutationFn: ({ expertTipId, feedbackType }) =>
      expertTipsApi.submitFeedback(expertTipId, feedbackType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expertTipKeys.all });
    },
  });
}
